import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aufgabenApi } from '../api';

const PRIORITY_LABEL = { critical: 'Kritisch', high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };
const PRIORITY_COLOR = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
const STATUS_LABEL = { open: 'Offen', today: 'Heute', in_progress: 'In Arbeit', waiting: 'Wartend', completed: 'Erledigt', dismissed: 'Ignoriert', overdue: 'Überfällig', archived: 'Archiviert' };
const STATUS_COLOR = { open: '#64748b', today: '#3b82f6', in_progress: '#8b5cf6', waiting: '#f59e0b', completed: '#22c55e', dismissed: '#94a3b8', overdue: '#ef4444', archived: '#475569' };
const CAT_LABEL = { contract: 'Vertrag', document: 'Dokument', deadline: 'Frist', cancellation: 'Kündigung', price: 'Preis', evidence: 'Nachweis', communication: 'Kommunikation', follow_up: 'Wiedervorlage', data_quality: 'Datenqualität', security: 'Sicherheit', system: 'System' };

const TABS = [
  { id: 'active', label: 'Aktiv', statuses: ['open', 'today', 'in_progress', 'overdue'] },
  { id: 'today', label: 'Heute', statuses: ['today'] },
  { id: 'overdue', label: 'Überfällig', statuses: ['overdue'] },
  { id: 'all', label: 'Alle', statuses: [] },
  { id: 'completed', label: 'Erledigt', statuses: ['completed'] },
];

export default function AufgabenPage() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tab = TABS.find(t => t.id === activeTab);
      const params = {};
      if (tab.statuses.length) params.status = tab.statuses.join(',');
      if (filterPriority) params.priority = filterPriority;
      if (filterCategory) params.category = filterCategory;
      if (search) params.q = search;
      params.limit = 100;
      const [tRes, sRes] = await Promise.all([
        aufgabenApi.list(params),
        aufgabenApi.statistik()
      ]);
      setTasks(tRes.data.tasks || []);
      setStats(sRes.data);
    } catch {
      setError('Aufgaben konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterPriority, filterCategory, search]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action, data = {}) => {
    try {
      if (action === 'complete') await aufgabenApi.erledigen(id, data.notiz);
      else if (action === 'dismiss') await aufgabenApi.ignorieren(id, data.notiz);
      else if (action === 'reopen') await aufgabenApi.wiedereroeffnen(id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Aktion fehlgeschlagen');
    }
  };

  const handleBulk = async (action) => {
    if (!selected.length) return;
    try {
      await aufgabenApi.bulk(selected, action);
      setSelected([]);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Bulk-Aktion fehlgeschlagen');
    }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#f1f5f9' }}>Aufgaben-Center</h2>
        <button
          onClick={() => navigate('/aufgaben/neu')}
          style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 600 }}
        >
          + Neue Aufgabe
        </button>
      </div>

      {/* Statistik-Badges */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Offen', value: stats.open_count || 0, color: '#64748b' },
            { label: 'Heute', value: stats.today_count || 0, color: '#3b82f6' },
            { label: 'Überfällig', value: stats.overdue_count || 0, color: '#ef4444' },
            { label: 'Kritisch', value: stats.critical_count || 0, color: '#dc2626' },
            { label: 'Diese Woche', value: stats.due_this_week || 0, color: '#8b5cf6' },
          ].map(b => (
            <div key={b.label} style={{ background: '#1e293b', borderRadius: 8, padding: '10px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, border: '1px solid #334155' }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: b.color }}>{b.value}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{b.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #334155' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeTab === t.id ? 700 : 400, borderBottom: activeTab === t.id ? '2px solid #818cf8' : '2px solid transparent', color: activeTab === t.id ? '#818cf8' : '#64748b' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen…"
          style={{ flex: 1, minWidth: 200, padding: '7px 12px', borderRadius: 6, border: '1px solid #334155', fontSize: 14, background: '#1e293b', color: '#f1f5f9' }} />
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid #334155', fontSize: 14, background: '#1e293b', color: '#94a3b8' }}>
          <option value="">Alle Prioritäten</option>
          {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid #334155', fontSize: 14, background: '#1e293b', color: '#94a3b8' }}>
          <option value="">Alle Kategorien</option>
          {Object.entries(CAT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Bulk-Aktionen */}
      {selected.length > 0 && (
        <div style={{ background: '#1e1b4b', border: '1px solid #818cf8', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600, color: '#c7d2fe' }}>{selected.length} ausgewählt</span>
          <button onClick={() => handleBulk('complete')} style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>Erledigen</button>
          <button onClick={() => handleBulk('dismiss')} style={{ background: '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>Ignorieren</button>
          <button onClick={() => setSelected([])} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>Abbrechen</button>
        </div>
      )}

      {error && <div style={{ background: '#431407', color: '#fca5a5', border: '1px solid #ef4444', padding: 12, borderRadius: 8, marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Lädt…</div>
      ) : tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Keine Aufgaben gefunden.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tasks.map(task => (
            <div key={task.id}
              style={{ background: '#1e293b', borderRadius: 10, border: '1px solid #334155', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}
              onClick={() => navigate(`/aufgaben/${task.id}`)}
            >
              <input type="checkbox" checked={selected.includes(task.id)}
                onClick={e => { e.stopPropagation(); toggleSelect(task.id); }}
                style={{ marginTop: 3, cursor: 'pointer' }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: '#f1f5f9' }}>{task.title}</span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: PRIORITY_COLOR[task.priority] + '22', color: PRIORITY_COLOR[task.priority], fontWeight: 700 }}>
                    {PRIORITY_LABEL[task.priority]}
                  </span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: STATUS_COLOR[task.status] + '22', color: STATUS_COLOR[task.status] }}>
                    {STATUS_LABEL[task.status]}
                  </span>
                  {task.automatic === 1 && <span style={{ fontSize: 10, color: '#94a3b8' }}>⚙ Auto</span>}
                </div>
                {task.description && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{task.description}</div>}
                <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
                  {task.vertrag_titel && <span>📄 {task.vertrag_titel}</span>}
                  {task.due_date && <span>📅 {new Date(task.due_date).toLocaleDateString('de-DE')}</span>}
                  <span>{CAT_LABEL[task.category] || task.category}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                {['completed', 'dismissed'].includes(task.status)
                  ? <button onClick={() => handleAction(task.id, 'reopen')} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer', background: '#334155', color: '#94a3b8' }}>Öffnen</button>
                  : <>
                    <button onClick={() => handleAction(task.id, 'complete')} style={{ fontSize: 12, padding: '4px 10px', border: 'none', borderRadius: 6, cursor: 'pointer', background: '#22c55e', color: '#fff' }}>✓</button>
                    <button onClick={() => handleAction(task.id, 'dismiss')} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer', background: '#334155', color: '#94a3b8' }}>×</button>
                  </>
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
